<template>
  <div class="BookingRecapHotel">
    <ai-typo
      v-if="hotel?.name"
      as="h1"
      class="BookingRecapHotel-name"
      color="gold-900"
      variant="heading-04">
      {{ hotel?.name }}
    </ai-typo>

    <ul>
      <!-- Location -->
      <li class="BookingRecapHotel-row">
        <ai-icon
          :aria-label="$t('booking.recap.icons.location')"
          :size="24"
          class="BookingRecapHotel-icon"
          name="location" />
        <ai-typo as="span" variant="paragraph-04-bold">
          {{ location }}
        </ai-typo>
      </li>

      <!-- Checkin/Checkout -->
      <li class="BookingRecapHotel-row">
        <ai-icon :size="24" class="BookingRecapHotel-icon" name="time" />
        <ai-typo as="span" variant="paragraph-04-bold">
          {{ checkInOut }}
        </ai-typo>
      </li>
    </ul>

    <ai-button
      :label="$t('booking.recap.hotel.viewHotelDetails')"
      :url="
        localePath({
          name: 'hotel-hotelSlug',
          params: { hotelSlug: data?.hotel?.slug },
          query: queryParameters,
        })
      "
      as-link
      color="gold-700"
      icon-right="arrow-right"
      target="_blank"
      variant="tertiary" />
  </div>
</template>

<script lang="ts" setup>
import { useHotelAsyncData } from '~/domains/graphql';
import { capitalize } from '~/helpers';
import { serializeRemainingGuests } from '~/domains/booking';

import { useBookingStore } from '../../store';

const route = useRoute();
const bookingStore = useBookingStore();
const { t, locale } = useI18n();

const { data } = useHotelAsyncData({
  hotelId:
    (route.params.ridcode as string) ??
    route.query.ridcode ??
    bookingStore.hotelId ??
    0,
});

const hotel = computed(() => data.value?.hotel);
const location = computed(() => {
  const country = hotel.value?.address?.country;
  const rawCity = hotel?.value?.address?.city;

  const city = rawCity ? capitalize(rawCity) : rawCity;

  return t('booking.recap.hotel.location', { city, country });
});

const translationCache: any = {};
const checkInOut = ref<string>('');

function translate(text: any, callback: any) {
  if (
      process.server ||
      !text ||
      locale.value === 'en' ||
      !(window as any).Weglot
  ) {
    if (callback) {
      callback(text);
    }
    return;
  }

  if (translationCache[text]) {
    if (callback) {
      callback(translationCache[text]);
    }
    return;
  }

  (window as any).Weglot.translate(
      { words: [{ t: 1, w: text }], languageTo: locale.value },
      function (data: any) {
        if (data && data.length && data.length !== 0) {
          translationCache[text] = data[0];
          if (callback) {
            callback(translationCache[text]);
          }
        }
      },
  );
}

const { dateIn, nights } = bookingStore.dates;
const remainingGuests = serializeRemainingGuests(bookingStore.remainingGuests);

const queryParameters = ref({});

if (dateIn && nights && remainingGuests) {
  queryParameters.value = {
    dateIn,
    nights,
    remainingGuests,
  };
}

watch(hotel, function() {
  const checkIn = hotel.value?.checkInHour ?? ' ';
  const checkOut = hotel.value?.checkOutHour ?? ' ';

  translate(t('booking.recap.hotel.checkInOut'), (txt: any) => {
    checkInOut.value = txt.replace('1', checkIn).replace('2', checkOut);
  });
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';

.BookingRecapHotel {
  padding: constants.$padding-01;
  background-color: colors.$gold-100;
}

.BookingRecapHotel-name {
  margin-bottom: constants.$inner-02;
}

.BookingRecapHotel-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: constants.$margin-00;

  &:last-of-type {
    margin-bottom: constants.$margin-01;
  }
}

.BookingRecapHotel-icon {
  color: colors.$gold-700;
  margin-right: constants.$inner-02;
}

.theme.rixos {
  .BookingRecapHotel {
    background: colors.$white;
  }
}
</style>
